import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Empty, Typography, Descriptions, Image } from "antd";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import LoadingIndicator from "../../Loaders/LoadingIndicator";
import ReactPlayer from "react-player";
import { Document, Page, pdfjs } from "react-pdf";
import dayjs from "dayjs";
import "./PatientGalleryScroll.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const isImageFile = (item) =>
  item?.type?.includes("image") ||
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(item?.path || "") ||
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(item?.file_name || "");

const getYoutubeVideoId = (url) => {
  if (!url || typeof url !== "string") return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (match) return match[1];
  const partial = url.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  return partial ? partial[1] : null;
};

const normalizeVideoUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (
    trimmed.includes("youtube.com") ||
    trimmed.includes("youtu.be") ||
    trimmed.includes("watch?v=")
  ) {
    return trimmed.startsWith("www.")
      ? `https://${trimmed}`
      : `https://www.youtube.com/${trimmed.replace(/^\//, "")}`;
  }
  return trimmed;
};

const isVideoItem = (item) => {
  const url = item?.url || item?.others_link || "";
  if (item?.type?.includes("video")) return true;
  if (item?.source_type === "other_source") return true;
  if (getYoutubeVideoId(url)) return true;
  const haystack = `${item?.path || ""} ${url} ${item?.file_name || ""}`;
  return /\.(mp4|mov|avi|webm|m4v)(\?|$)/i.test(haystack);
};

function useNarrowViewport(maxWidth = 768) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= maxWidth
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    window.addEventListener("resize", sync);
    return () => {
      mq.removeEventListener("change", sync);
      window.removeEventListener("resize", sync);
    };
  }, [maxWidth]);

  return narrow;
}

const PrevArrow = ({ onClick }) => (
  <div className="custom-arrow custom-prev" onClick={onClick}>
    <LeftOutlined />
  </div>
);

const NextArrow = ({ onClick }) => (
  <div className="custom-arrow custom-next" onClick={onClick}>
    <RightOutlined />
  </div>
);

const GalleryLightboxImage = ({ item, isNarrowViewport }) => {
  const label =
    item?.file_name || item?.path?.split("/").pop() || "Gallery preview";

  return (
    <div
      className={`gallery-lightbox-media inv-slider-img-container${
        isNarrowViewport ? " gallery-lightbox-media-mobile" : ""
      }`}
    >
      <Image
        src={item?.url}
        alt={label}
        className="gallery-lightbox-img inv-slider-img"
        preview={{
          mask: (
            <span className="gallery-lightbox-preview-mask">
              <ZoomInOutlined /> Preview
            </span>
          ),
        }}
      />
    </div>
  );
};

const GalleryLightboxPdf = ({ url, fileName, isNarrowViewport }) => {
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const frameRef = useRef(null);
  const [pageWidth, setPageWidth] = useState(720);

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const apply = (width) => {
      if (width > 0) setPageWidth(Math.max(240, Math.floor(width - 24)));
    };

    apply(el.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      apply(entries[0]?.contentRect?.width ?? 0);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleOpenInNewTab = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={`gallery-lightbox-pdf${
        isNarrowViewport ? " gallery-lightbox-pdf-mobile" : ""
      }`}
    >
      <div className="gallery-lightbox-pdf-toolbar">
        <Typography.Text className="gallery-lightbox-pdf-hint">
          Scroll to view all pages
        </Typography.Text>
        <Button
          type="link"
          icon={<ExportOutlined />}
          onClick={handleOpenInNewTab}
          disabled={!url}
          className="gallery-lightbox-pdf-open"
        >
          Open in new tab
        </Button>
      </div>

      <div
        ref={frameRef}
        className="gallery-lightbox-pdf-frame-wrap patient-gallery-pdf-scroll"
      >
        {!url || loadError ? (
          <div className="gallery-lightbox-pdf-loading">
            <Empty description="Cannot load PDF file" />
          </div>
        ) : (
          <Document
            file={url}
            loading={
              <div className="gallery-lightbox-pdf-loading">
                <LoadingIndicator message="Loading PDF, please wait..." />
              </div>
            }
            onLoadSuccess={({ numPages: totalPages }) => {
              setNumPages(totalPages);
              setLoadError(false);
            }}
            onLoadError={() => setLoadError(true)}
            error={
              <div className="gallery-lightbox-pdf-loading">
                <Empty description="Cannot load PDF file" />
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, index) => (
              <Page
                key={`pdf-page-${index + 1}`}
                pageNumber={index + 1}
                width={pageWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            ))}
          </Document>
        )}
      </div>
    </div>
  );
};

const PatientGalleryScroll = ({
  selectedGroup,
  selectedItem,
  exercises = false,
}) => {
  const isNarrowViewport = useNarrowViewport(768);
  const data = selectedGroup;

  const getFileLabel = (item) => {
    if (!item) return "";
    const name =
      item.file_name ||
      item.name ||
      (typeof item.path === "string" ? item.path.split("/").pop() : "") ||
      "";
    return String(name).trim();
  };

  const renderExerciseDetails = (item) => {
    if (!exercises) return null;

    return (
      <div className="gallery-lightbox-exercise-details">
        <Descriptions
          title="Exercise Details"
          layout="vertical"
          column={isNarrowViewport ? 1 : 4}
          bordered
          size="small"
          items={[
            {
              key: "frequency",
              label: "Frequency",
              children: item?.frequency || "Not specified",
            },
            {
              key: "intensity",
              label: "Intensity",
              children: item?.intensity || "Not specified",
            },
            {
              key: "startDate",
              label: "Start Date",
              children: item?.start_date
                ? dayjs(item.start_date).format("DD/MM/YYYY")
                : "Not specified",
            },
            {
              key: "endDate",
              label: "End Date",
              children:
                item?.start_date && item?.duration != null
                  ? dayjs(item.start_date)
                      .add(item.duration, "day")
                      .format("DD/MM/YYYY")
                  : "Not specified",
            },
          ]}
        />
      </div>
    );
  };

  const renderMedia = (item) => {
    if (isVideoItem(item)) {
      const videoUrl = normalizeVideoUrl(item?.url || item?.others_link || "");
      return (
        <div
          className={`gallery-lightbox-media gallery-lightbox-video${
            exercises ? " gallery-lightbox-video-exercises" : ""
          }`}
        >
          <ReactPlayer
            className="gallery-lightbox-react-player"
            url={videoUrl}
            width="100%"
            height="100%"
            controls
            playsinline
            config={{
              youtube: {
                playerVars: { playsinline: 1, rel: 0 },
              },
              file: {
                attributes: {
                  controls: true,
                  playsInline: true,
                },
              },
            }}
          />
        </div>
      );
    }

    if (isImageFile(item)) {
      return <GalleryLightboxImage item={item} isNarrowViewport={isNarrowViewport} />;
    }

    return (
      <GalleryLightboxPdf
        key={item?.url}
        url={item?.url}
        fileName={getFileLabel(item)}
        isNarrowViewport={isNarrowViewport}
      />
    );
  };

  const settings = {
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: data.length > 1,
    initialSlide: data.length > 1 ? selectedItem : 0,
    swipe: !isNarrowViewport && !exercises,
    touchMove: !isNarrowViewport && !exercises,
    adaptiveHeight: Boolean(exercises),
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  return (
    <div
      className={`patient-gallery-preview-root${
        exercises ? " patient-gallery-preview-root-exercises" : ""
      }`}
    >
      {data?.length === 1 ? (
        <div className="gallery-lightbox-single">
          {renderMedia(data[0])}
          {renderExerciseDetails(data[0])}
        </div>
      ) : (
        <Slider {...settings} className="patient-gallery-slider" dots={false}>
          {data?.map((item, index) => (
            <div key={index} className="gallery-lightbox-slide">
              {renderMedia(item)}
              {renderExerciseDetails(item)}
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default PatientGalleryScroll;
